import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { space } from "@tokens/index";
import { SkeletonRows, Switch, Text } from "@primitives/index";
import { SettingsGroup, SettingsRow } from "@compositions/index";
import { usePushRegistration, useQueueNotificationPreferences, type PushToggleStatus } from "../push";
import { useMyQueues } from "../queues";
import { ErrorAlert } from "../admin-ui";
import { SettingsPage } from "./SettingsPage";

/**
 * Notifications: the device-scoped push toggle and the per-queue mutes.
 *
 * The device switch binds to `usePushRegistration`. When the browser cannot
 * deliver push, or the user blocked it, the switch is disabled and the row
 * description states the reason in plain words. The queue switches bind to
 * `useQueueNotificationPreferences`; "notify" is the inverse of "muted".
 */

interface DeviceRowState {
    readonly description: string;
    readonly disabled: boolean;
}

function deviceRowState(available: boolean, status: PushToggleStatus): DeviceRowState {
    if (!available) {
        return { description: "Push is not set up for this environment.", disabled: true };
    }
    switch (status) {
        case "unsupported":
            return { description: "This browser cannot show notifications.", disabled: true };
        case "denied":
            return {
                description: "Blocked by the browser. Allow notifications for this site to enable.",
                disabled: true,
            };
        case "on":
            return { description: "This device receives alerts for your queues.", disabled: false };
        case "off":
            return { description: "Get alerts on this device when work arrives.", disabled: false };
        case "busy":
            return { description: "Updating\u2026", disabled: false };
    }
}

const stateBlockStyle: CSSProperties = {
    padding: `${space[3]} ${space[4]}`,
};

export function NotificationsSettingsPage(): ReactElement {
    const push = usePushRegistration();
    const queuesQuery = useMyQueues();
    const mutes = useQueueNotificationPreferences(push.available);

    // While a round-trip is in flight the switch keeps the value it had, so
    // the thumb does not jump before the spinner appears.
    const [settledOn, setSettledOn] = useState(push.status === "on");
    useEffect(() => {
        if (push.status === "on") setSettledOn(true);
        else if (push.status === "off") setSettledOn(false);
    }, [push.status]);

    const device = deviceRowState(push.available, push.status);
    const deviceChecked = push.status === "on" || (push.status === "busy" && settledOn);
    const queues = queuesQuery.data?.items;

    return (
        <SettingsPage
            title="Notifications"
            description="Push alerts for this device and the queues you belong to."
        >
            <SettingsGroup title="This device">
                <SettingsRow
                    id="settings-push"
                    label="Push notifications"
                    description={device.description}
                    testId="settings-push-row"
                >
                    {({ labelId, descriptionId }) => (
                        <Switch
                            checked={deviceChecked}
                            onCheckedChange={() => push.toggle()}
                            disabled={device.disabled}
                            busy={push.status === "busy"}
                            aria-labelledby={labelId}
                            {...(descriptionId !== undefined
                                ? { "aria-describedby": descriptionId }
                                : {})}
                        />
                    )}
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup
                title="Queues"
                description="Choose which queues may alert you. Applies on all your devices."
                testId="settings-queues"
            >
                {queues === undefined && queuesQuery.isLoading ? (
                    <div style={stateBlockStyle}>
                        <SkeletonRows rows={3} label="Loading queues" />
                    </div>
                ) : queuesQuery.error !== null && queues === undefined ? (
                    <div style={stateBlockStyle}>
                        <ErrorAlert onRetry={() => void queuesQuery.refetch()}>
                            Queues could not be loaded.
                        </ErrorAlert>
                    </div>
                ) : queues === undefined || queues.length === 0 ? (
                    <div style={stateBlockStyle}>
                        <Text as="p" role="body" tone="muted">
                            You are not a member of any queue.
                        </Text>
                    </div>
                ) : (
                    queues.map((queue) => (
                        <SettingsRow
                            key={queue.id}
                            id={`settings-queue-${queue.id}`}
                            label={queue.name}
                            {...(queue.isPersonal ? { description: "Personal queue" } : {})}
                            testId={`settings-queue-${queue.id}`}
                        >
                            {({ labelId, descriptionId }) => (
                                <Switch
                                    checked={!mutes.mutedQueueIds.has(queue.id)}
                                    onCheckedChange={() => mutes.toggleMute(queue.id)}
                                    disabled={!push.available}
                                    aria-labelledby={labelId}
                                    {...(descriptionId !== undefined
                                        ? { "aria-describedby": descriptionId }
                                        : {})}
                                />
                            )}
                        </SettingsRow>
                    ))
                )}
            </SettingsGroup>
        </SettingsPage>
    );
}
